import { useEffect, useRef } from 'react'

type RectDimensions = {
  top: number
  left: number
  width: number
  height: number
}

const initialSize: Partial<RectDimensions> = {
  top: undefined,
  left: undefined,
  width: undefined,
  height: undefined,
}

export function useOnResizeObserver<T extends HTMLElement = HTMLElement>(
  ref: T,
  onResize: (rect: RectDimensions) => void,
) {
  const previousRectDimensions = useRef<Partial<RectDimensions>>({
    ...initialSize,
  })

  useEffect(() => {
    const target = ref
    if (!target) return

    if (typeof window === 'undefined' || !('ResizeObserver' in window)) return

    const observer = new ResizeObserver(([entry]) => {
      const { top, left, width, height } = entry.target.getBoundingClientRect()
      const hasChanged =
        previousRectDimensions.current.top !== top ||
        previousRectDimensions.current.left !== left ||
        previousRectDimensions.current.width !== width ||
        previousRectDimensions.current.height !== height

      if (hasChanged) {
        previousRectDimensions.current.top = top
        previousRectDimensions.current.left = left
        previousRectDimensions.current.width = width
        previousRectDimensions.current.height = height

        const newDimensions: RectDimensions = { top, left, width, height }
        onResize(newDimensions)
      }
    })

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [ref, onResize])
}
